import { RESOURCES_NAMES } from "@/const/resource.constants"
import {
  useCreate,
  useDelete,
  useHttp,
  useUpdate,
  type Config
} from "@/hooks/useHttp"

import type {
  AiMcpServerScopeOptionsResponse,
  AiPoliciesResponse,
  AiPolicyDecisions,
  AiPolicyDecisionsParams,
  AiPolicyOptionsResponse,
  AiPolicyRequest,
  AiPolicyResponse
} from "./useAiPolicies.types"

const {
  LIST,
  SCOPE_OPTIONS,
  MCP_SERVER_SCOPE_OPTIONS,
  CONDITIONS_OPTIONS,
  DECISIONS
} = RESOURCES_NAMES.AI_POLICIES

// The API lists agent policies alone unless asked for other kinds too
const ALL_KINDS = { kind: "agent,mcpServer" }

export const useGetAiPolicies = <S = AiPoliciesResponse>(
  config?: Config<AiPoliciesResponse, S>
) => useHttp<AiPoliciesResponse, S>(LIST, ALL_KINDS, config)

// The policies the list shows: filtered and ordered by the API. The rows stay
// while a new filter or order loads
export const useListAiPolicies = <S = AiPoliciesResponse>(
  params: Record<string, string> | undefined,
  config?: Config<AiPoliciesResponse, S>
) =>
  useHttp<AiPoliciesResponse, S>(LIST, params && { ...ALL_KINDS, ...params }, {
    placeholderData: (previous) => previous,
    ...config,
    enabled: !!params && config?.enabled !== false
  })

export const useCreateAiPolicy = () =>
  useCreate<AiPolicyResponse, AiPolicyRequest>(LIST)

// Takes { needle: `/${id}`, data }
export const useUpdateAiPolicy = () =>
  useUpdate<AiPolicyResponse, { needle: string; data: AiPolicyRequest }>(LIST)

// Takes the policy id as the needle
export const useDeleteAiPolicy = () => useDelete<unknown>(LIST)

export const useGetAiPolicyScopeOptions = <S = AiPolicyOptionsResponse>(
  config?: Config<AiPolicyOptionsResponse, S>
) => useHttp<AiPolicyOptionsResponse, S>(SCOPE_OPTIONS, undefined, config)

// The MCP servers an MCP server policy can name, with where each runs
export const useGetAiPolicyMcpServerScopeOptions = <
  S = AiMcpServerScopeOptionsResponse
>(
  config?: Config<AiMcpServerScopeOptionsResponse, S>
) =>
  useHttp<AiMcpServerScopeOptionsResponse, S>(
    MCP_SERVER_SCOPE_OPTIONS,
    undefined,
    config
  )

export const useGetAiPolicyConditionOptions = <S = AiPolicyOptionsResponse>(
  config?: Config<AiPolicyOptionsResponse, S>
) => useHttp<AiPolicyOptionsResponse, S>(CONDITIONS_OPTIONS, undefined, config)

export const useGetAiPolicyDecisions = (
  params: AiPolicyDecisionsParams,
  config?: Config<AiPolicyDecisions>
) =>
  useHttp<AiPolicyDecisions>(DECISIONS, params, {
    placeholderData: (previous) => previous,
    ...config
  })
